import AiStudioLogo from 'assets/images/others/ai-studio.svg';
import DamLogo from 'assets/images/others/dam.svg';
import ProductConfiguratorLogo from 'assets/images/others/product-configurator.svg';
import TryOnLogo from 'assets/images/others/tryon.svg';
import VisualCustomizerLogo from 'assets/images/others/visual-customizer.svg';
import solutionsAiStudio from 'assets/images/onboarding/ai-studio.mp4';
import solutionsConfigurator from 'assets/images/onboarding/configurator.mp4';
import solutionsCustomizer from 'assets/images/onboarding/customizer.mp4';
import solutionsTryon from 'assets/images/onboarding/tryon.mp4';
import solutionsViewer from 'assets/images/onboarding/viewer.mp4';
import React from 'react';
import { T, backofficeUrl } from 'src/shared/helpers';

// The catalogue the onboarding prompt routes into. These mirror the solutions shown on the dashboard
// (components/pages/dashboard/solutions/solutions.tsx) so a suggestion always lands on a tool the
// seller can also find there afterwards.
export type OnboardingTool = {
    id: 'VC' | 'PC' | 'AI' | 'VTO' | 'DAM';
    name: string;
    // What the seller gets out of the solution: the headline of its card on the solutions page and of
    // the suggestion.
    goal: string;
    // How it gets there, in a couple of sentences under the goal on the solutions page.
    summary: string;
    // Who and what the solution is made for, closing its card on the solutions page.
    builtFor: string;
    ctaLabel: string;
    // The same action for a seller who reopened the solutions view from inside the app, and so has
    // already started: they go to the tool rather than start with it.
    goToLabel: string;
    url: string;
    // A light tint of the logo's hue, behind the preview on the solutions view until the clip loads.
    tint: string;
    // The solution's brand mark, the same the dashboard solutions page shows.
    logo: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
    // A looping clip of the solution.
    video: string;
    // The three main features, listed under the goal on the suggestion. The part between ** markers is
    // set in bold.
    features: string[];
};

export const ONBOARDING_TOOLS: OnboardingTool[] = [
    {
        id: 'VC',
        name: T._('Visual Product Customizer', 'Admin'),
        goal: T._('Take custom orders with no artwork back-and-forth', 'Admin'),
        summary: T._(
            'Customers personalize in real time, within the rules you set. Every order lands with a validated, production-ready output, so nothing gets fixed by hand before production.',
            'Admin'
        ),
        builtFor: T._('Printers, decorators and POD sellers — apparel, promo, packaging, stationery', 'Admin'),
        ctaLabel: T._('Start with the Customizer', 'Admin'),
        goToLabel: T._('Go to the Customizer', 'Admin'),
        url: backofficeUrl('/dashboard'),
        tint: '#e6f2ff',
        logo: VisualCustomizerLogo,
        video: solutionsCustomizer,
        features: [
            T._('**Real-time personalization** on your product page', 'Admin'),
            T._('**Design rules** that keep every order valid', 'Admin'),
            T._('**Production-ready files** with every order', 'Admin')
        ]
    },
    {
        id: 'PC',
        name: T._('3D Product Configurator', 'Admin'),
        goal: T._('Sell made-to-order products', 'Admin'),
        summary: T._(
            'Shoppers combine components, materials and finishes in real time, with your production rules enforced at each step. Complex builds get priced on the spot and reach the floor ready to make.',
            'Admin'
        ),
        builtFor: T._('Manufacturers and made-to-order brands — furniture, footwear, jewelry, equipment', 'Admin'),
        ctaLabel: T._('Start with the Configurator', 'Admin'),
        goToLabel: T._('Go to the Configurator', 'Admin'),
        url: backofficeUrl('/landings/configurator'),
        tint: '#ffece6',
        logo: ProductConfiguratorLogo,
        video: solutionsConfigurator,
        features: [
            T._('**Interactive 3D configuration** of your products', 'Admin'),
            T._('**Every variant** from components, materials and colors', 'Admin'),
            T._('**Rule-based logic** that allows only valid combinations', 'Admin')
        ]
    },
    {
        id: 'AI',
        name: T._('AI Agents Studio', 'Admin'),
        goal: T._('Scale product visuals without scaling production', 'Admin'),
        summary: T._(
            'A team of AI agents turns the assets you already have into photos, videos and ad creatives in seconds. New visuals no longer wait on a studio booking.',
            'Admin'
        ),
        builtFor: T._('Marketing, creative and e-commerce teams — any catalogue, any channel', 'Admin'),
        ctaLabel: T._('Start with AI Agents Studio', 'Admin'),
        goToLabel: T._('Go to AI Agents Studio', 'Admin'),
        url: backofficeUrl('/ai-creative-tools/studio'),
        tint: '#f3e8ff',
        logo: AiStudioLogo,
        video: solutionsAiStudio,
        features: [
            T._('**Mockups and renders** from your product images', 'Admin'),
            T._('**Videos and custom content** for your store and campaigns', 'Admin'),
            T._('**No photo shoot** needed', 'Admin')
        ]
    },
    {
        id: 'DAM',
        name: T._('3D DAM & AR Viewer', 'Admin'),
        goal: T._('Centralize your 3D assets', 'Admin'),
        summary: T._(
            'Turn product photos or CAD files into 3D web-ready models and keep them optimized in a single library. Publish them to any channel without writing code.',
            'Admin'
        ),
        builtFor: T._('Teams scaling a 3D catalogue — fashion, furniture, manufacturing', 'Admin'),
        ctaLabel: T._('Start with the 3D DAM & AR Viewer', 'Admin'),
        goToLabel: T._('Go to the 3D DAM & AR Viewer', 'Admin'),
        url: backofficeUrl('/DAM?page=/gallery'),
        tint: '#eafadc',
        logo: DamLogo,
        video: solutionsViewer,
        features: [
            T._('**All your 3D assets** managed in one place', 'Admin'),
            T._('**Models optimized** for the web', 'Admin'),
            T._('**3D and AR viewing** on every channel', 'Admin')
        ]
    },
    {
        id: 'VTO',
        name: T._('Virtual Try-on', 'Admin'),
        goal: T._('Let shoppers try products on, straight from the browser', 'Admin'),
        summary: T._(
            'Hyper-realistic WebAR try-on with nothing to download. AI reads head size and pupillary distance so the fit is true to life, and fewer orders come back as returns.',
            'Admin'
        ),
        builtFor: T._('Eyewear, headwear, earrings, necklaces, shoes and scarves', 'Admin'),
        ctaLabel: T._('Start with Virtual Try-on', 'Admin'),
        goToLabel: T._('Go to Virtual Try-on', 'Admin'),
        url: backofficeUrl('/virtual-try-on'),
        tint: '#fde7f6',
        logo: TryOnLogo,
        video: solutionsTryon,
        features: [
            T._('**App-free**: it runs right in the browser', 'Admin'),
            T._('**AI-powered try-on** in real time', 'Admin'),
            T._('For **eyewear, jewelry, hats** and more', 'Admin')
        ]
    }
];

// These ready-made goals have a fixed solution; only free text needs a recommendation request.
export const PROMPT_PRESETS: { label: string; toolId: OnboardingTool['id'] }[] = [
    { label: T._('I want to automate the sale of printed, engraved or embroidered products', 'Admin'), toolId: 'VC' },
    { label: T._('I want to sell or showcase configurable made-to-order products', 'Admin'), toolId: 'PC' },
    { label: T._('I want product photos, videos and mockups without a photo shoot', 'Admin'), toolId: 'AI' },
    { label: T._('I want to digitize my products and show them in 3D and AR', 'Admin'), toolId: 'DAM' }
];

// The example in the free-text box, on the form and on the solutions view.
export const PROMPT_PLACEHOLDER = T._('E.g. I sell t-shirts and I want my customers to add their own text and logo before ordering.', 'Admin');

export type ToolSuggestion = {
    tool: OnboardingTool;
};
